import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// VISITS-3 (#176): shared scoring-display bits — group/total percentage
// badge coloring, the small "(+5)"-style delta, and the "biathlon" dot row.
// Percentages/totals are never clamped on the backend (weights can be
// negative — see plugins/visits/models.py) so all of this must render
// correctly below 0% and above 100%, not just assume a 0-100 range.

/** `inverted` — группа с отрицательным коэффициентом («Вредные привычки»): там
 *  0 % — лучший результат, цвет считаем от 100 − pct, как отчёт визита на бэкенде. */
export function pctColorClass(pct: number | null, inverted = false): string {
  if (pct === null) return 'text-muted-foreground';
  if (inverted) pct = 100 - pct;
  if (pct < 50) return 'text-destructive';
  if (pct < 80) return 'text-warning';
  return 'text-success';
}

function pctBadgeVariant(pct: number | null): 'destructive' | 'warning' | 'success' | 'muted' {
  if (pct === null) return 'muted';
  if (pct < 50) return 'destructive';
  if (pct < 80) return 'warning';
  return 'success';
}

export function formatPct(pct: number | null): string {
  if (pct === null) return '—';
  const rounded = Math.round(pct * 10) / 10;
  return `${rounded}%`;
}

/** Compact weight display for the small "(1/0)"-style hint next to a
 *  parameter name — round to 2 decimals and drop a trailing ".00"/".0" so
 *  fraction-entered weights (e.g. 1/3 -> 0.33) stay readable. */
export function formatWeight(weight: number): string {
  const rounded = Math.round(weight * 100) / 100;
  return String(rounded);
}

/** "+2" / "-1" / "0" — the ACHIEVED points fact for one answer, shown on
 *  the read-only visit view instead of the weight_yes/weight_no potential
 *  (Alexey 2026-08-27: "на просмотре должно показываться уже факт"). */
export function formatPoints(points: number): string {
  const rounded = Math.round(points * 100) / 100;
  return rounded > 0 ? `+${rounded}` : String(rounded);
}

export function TotalScoreBadge({ total, className }: { total: number | null; className?: string }) {
  return (
    <Badge variant={pctBadgeVariant(total)} className={cn('text-sm', className)}>
      {formatPct(total)}
    </Badge>
  );
}

/** "(+5)" / "(-3)" / "(0)" — colored green/red/muted by sign. */
export function DeltaChip({ delta }: { delta: number | null }) {
  if (delta === null) return null;
  const rounded = Math.round(delta * 10) / 10;
  if (rounded === 0) {
    return <span className="text-xs text-muted-foreground">(0)</span>;
  }
  const sign = rounded > 0 ? '+' : '';
  return (
    <span className={cn('text-xs font-medium tabular-nums', rounded > 0 ? 'text-success' : 'text-destructive')}>
      ({sign}{rounded})
    </span>
  );
}

/** Current% with the previous visit's value + delta shown small alongside,
 *  per Alexey: "маленькое рядом предыдущее значение и в скобках (+5)". */
export function PercentWithDelta({
  pct,
  previousPct,
  delta,
}: {
  pct: number | null;
  previousPct: number | null;
  delta: number | null;
}) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={cn('font-semibold tabular-nums', pctColorClass(pct))}>{formatPct(pct)}</span>
      {previousPct !== null && (
        <span className="inline-flex items-baseline gap-1 text-xs text-muted-foreground tabular-nums">
          {formatPct(previousPct)}
          <DeltaChip delta={delta} />
        </span>
      )}
    </span>
  );
}

/** The "biathlon": a row of dots, oldest→newest, last one = current answer.
 *  Alexey: "ряд кружков, последний — текущий". Filled green = yes, filled
 *  red = no, hollow = unanswered at that visit. */
export function BiathlonDots({
  history,
  size = 'sm',
  inverted = false,
}: {
  history: (boolean | null)[];
  size?: 'sm' | 'md';
  /** Negative-coefficient parameters (e.g. "Вредные привычки") score "yes"
   *  as the bad outcome — flip green/red accordingly, same as the yes/no
   *  control (Alexey 2026-08-26). */
  inverted?: boolean;
}) {
  if (history.length === 0) return null;
  const dot = size === 'sm' ? 'size-2' : 'size-2.5';
  const yesIsGood = !inverted;
  return (
    <span className="inline-flex items-center gap-1" role="img" aria-label="history">
      {history.map((value, i) => {
        const isLast = i === history.length - 1;
        const good = value === true ? yesIsGood : value === false ? !yesIsGood : null;
        return (
          <span
            key={i}
            className={cn(
              dot,
              'rounded-full',
              isLast && 'ring-2 ring-offset-1 ring-offset-background',
              good === true && cn('bg-success', isLast && 'ring-success/50'),
              good === false && cn('bg-destructive', isLast && 'ring-destructive/50'),
              value === null && 'border border-border bg-transparent',
            )}
          />
        );
      })}
    </span>
  );
}
